// Users — 真實資料版（Route 7, PR #97）。
//
// 後端 GET/PATCH/DELETE /v1/admin/users (tenant-api) joined with nexus_control.users.
// 邀請新成員：「邀請使用者」按鈕開 BindingCodeIssueModal — sidebar 不再
// 有「邀請碼」 條目，這頁是主要動線。歷史 / 撤銷邀請碼仍可走
// /dashboard/admin/binding-codes（URL 保留）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, KeyRound, ShieldAlert, ShieldCheck, RefreshCw, ServerCrash, Inbox, Building2, Trash2, MapPin, UserCheck, Smartphone, Ban, CheckCircle2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import * as SharedRbac from "@nexus/shared/rbac";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import BindingCodeIssueModal from "../components/BindingCodeIssueModal.js";
import { apiFetch } from "../../lib/api-client.js";
import { formatTaipeiDateTime, formatTaipeiAgo } from "../../lib/time.js";
import {
  listAdminUsers, patchMembership, revokeMembership, friendlyUsersError,
  listOfficers, getOfficerSites, putOfficerSites,
  type MembershipRow, type UserRoleV2, type UserStatus, type OfficerRow,
} from "../lib/usersApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { listAdminDevices, patchDeviceStatus, type DeviceRow, type DeviceStatus } from "../lib/devicesApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import OfficerEnrollDrawer from "../components/OfficerEnrollDrawer.js";
import UserRolesTab from "../components/UserRolesTab.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { roleLabel } from "../lib/roleDisplay.js";

const ROLE_LABEL = (r: UserRoleV2): string => roleLabel(r);
const ROLE_BADGE: Record<UserRoleV2, string> = {
  tenant_admin: "badge-accent",
  manager: "badge-warning",
  officer: "badge-default",
  site_supervisor: "badge-warning",
  incident_reporter: "badge-default",
  auditor: "badge-default",
};
// Roles that perform on-site SOPs themselves (感應地點、執行任務): need
// an officers row + face/passkey enrollment + site assignments to limit
// where they can patrol. Spec — every tenant-scoped role except
// super_admin is enrollable (some operator teams promote managers /
// admins to fill in when staffing is thin, and even auditors do
// spot-check patrols). See `@nexus/shared` ENROLLABLE_ROLES for
// the canonical set; this `Set` here is just an O(1)-lookup wrapper
// over it. auth-api/binding-redeem + tenant-api/admin-users consult
// the same constant when deciding whether to upsert the officers row.
const ENROLLABLE_ROLES = new Set<UserRoleV2>(SharedRbac.ENROLLABLE_ROLES as readonly UserRoleV2[]);
const STATUS_LABEL: Record<UserStatus, string> = { active: "啟用", invited: "已邀請", revoked: "已停用" };
const STATUS_BADGE: Record<UserStatus, string> = { active: "badge-success", invited: "badge-warning", revoked: "badge-default" };

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: MembershipRow[]; total: number }
  | { status: "error"; message: string };

type DrawerState = null | { row: MembershipRow };
type AssignState = null | { row: MembershipRow };
type EnrollState = null | { row: MembershipRow; officerId: string };

export default function Users(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:user") || session.scopes.includes("admin:tenant") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const [assigning, setAssigning] = useState<AssignState>(null);
  const [enrolling, setEnrolling] = useState<EnrollState>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<number | null>(null);

  const pag = usePagination({ urlPrefix: "users_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listAdminUsers({ tenantId: tenantParam, limit: pag.limit, offset: pag.offset });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlyUsersError(err) });
    }
  }, [tenantParam, pag.limit, pag.offset]);

  const loadPendingInvites = useCallback(async () => {
    try {
      const r = await apiFetch(`/v1/invites?status=active&limit=100`, { method: "GET", credentials: "include" });
      const body = await r.json().catch(() => ({})) as { ok?: boolean; results?: unknown[] };
      if (r.ok && Array.isArray(body.results)) setPendingInvites(body.results.length);
    } catch { /* silent — chip just won't show */ }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (canWrite) void loadPendingInvites(); }, [canWrite, loadPendingInvites]);

  const columns: Column<MembershipRow>[] = useMemo(() => [
    {
      key: "user", header: "使用者", primary: true, width: "30%",
      render: (u) => (
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-subtle text-accent text-2xs font-semibold">
            {(u.display_name ?? u.email).slice(0, 1).toUpperCase()}
          </span>
          <div>
            <div className="font-medium text-text-primary">{u.display_name ?? u.email.split("@")[0]}</div>
            <div className="text-2xs text-text-muted font-mono">{u.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "role", header: "角色",
      render: (u) => {
        const r = (u.role as UserRoleV2);
        const label = ROLE_LABEL(r);
        const cls = ROLE_BADGE[r] ?? "badge-default";
        return <span className={cls}>{label}</span>;
      },
    },
    {
      key: "sso", header: "登入方式", hideOnMobile: true,
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          {u.sso_providers.length === 0 && <span className="text-2xs text-text-muted">Email</span>}
          {u.sso_providers.map((p) => (
            <span key={p} className="badge-default text-2xs">{p === "google" ? "Google" : p === "apple" ? "Apple" : p === "line" ? "LINE" : p}</span>
          ))}
          {u.webauthn_credentials > 0 && (
            <span className="badge-success inline-flex items-center gap-1"><KeyRound className="h-3 w-3" />{u.webauthn_credentials}</span>
          )}
        </div>
      ),
    },
    {
      key: "status", header: "狀態",
      render: (u) => <span className={STATUS_BADGE[u.status]}>{STATUS_LABEL[u.status]}</span>,
    },
    {
      key: "lastLogin", header: "最後登入", hideOnMobile: true,
      render: (u) => <span className="font-mono text-2xs text-text-secondary">{formatTaipeiDateTime(u.last_login_at)}</span>,
    },
    {
      key: "officer_actions", header: "執勤設定", width: "16%",
      render: (u) => {
        const canEnroll = ENROLLABLE_ROLES.has(u.role as UserRoleV2);
        if (!canEnroll) return <span className="text-2xs text-text-muted">—</span>;
        if (!u.line_user_id) {
          return <span className="text-2xs text-text-muted" title="尚未綁定 LINE">未綁定 LINE</span>;
        }
        return (
          <div className="inline-flex gap-1">
            <button
              className="btn-secondary text-2xs py-1 px-2 inline-flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); setAssigning({ row: u }); }}
              title="指派此執勤者可巡邏的案場"
            >
              <MapPin className="h-3 w-3" /> 案場
            </button>
            <button
              className="btn-secondary text-2xs py-1 px-2 inline-flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); setEnrolling({ row: u, officerId: "" }); }}
              title="產生人臉 / Passkey 註冊連結"
            >
              <UserCheck className="h-3 w-3" /> 註冊
            </button>
          </div>
        );
      },
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="使用者" description="此單位內的成員、角色與裝置" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="使用者"
        description="此單位內的成員、角色與登入方式"
        actions={
          <>
            {pendingInvites !== null && pendingInvites > 0 && (
              <Link
                to="/dashboard/admin/binding-codes"
                className="inline-flex items-center gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1 text-2xs text-warning hover:bg-warning/15"
                title="尚未兌換的邀請碼"
              >
                <KeyRound className="h-3 w-3" />
                待兌換邀請：{pendingInvites >= 100 ? "99+" : pendingInvites}
              </Link>
            )}
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button
                data-tour-id="users.invite"
                type="button"
                onClick={() => setInviteOpen(true)}
                className="btn-primary text-sm"
              >
                <Plus className="h-4 w-4" /> 邀請使用者
              </button>
            )}
          </>
        }
      />
      {state.status === "loading" && <UsersSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="使用者列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={Inbox} title="這個單位還沒有任何成員" description="請先建立邀請碼讓成員加入。"
          action={canWrite ? (
            <button data-tour-id="users.invite" type="button" onClick={() => setInviteOpen(true)} className="btn-primary text-sm">
              建立邀請碼
            </button>
          ) : undefined} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <DataTable
            rows={state.rows}
            columns={columns}
            rowKey={(r) => r.id}
            searchableText={(r) => `${r.email} ${r.display_name ?? ""} ${r.role}`}
            onRowClick={canWrite ? (r) => setEditing({ row: r }) : undefined}
            serverPagination
          />
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={state.total}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `編輯 ${editing.row.display_name || editing.row.email || "使用者"}` : "編輯使用者"}
        description="變更角色或停用此使用者"
      >
        {editing && (
          <UserForm
            row={editing.row}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
          />
        )}
      </Drawer>
      <Drawer
        open={!!assigning}
        onClose={() => setAssigning(null)}
        title={assigning ? `指派案場 — ${assigning.row.display_name ?? assigning.row.email}` : "指派案場"}
        description="勾選此執勤者可巡邏的案場（可多選）。未勾選者將被撤銷指派。"
      >
        {assigning && (
          <AssignSitesForm
            row={assigning.row}
            tenantId={tenantParam}
            onClose={() => setAssigning(null)}
            onSaved={() => { setAssigning(null); void load(); }}
          />
        )}
      </Drawer>
      <Drawer
        open={!!enrolling}
        onClose={() => setEnrolling(null)}
        title={enrolling ? `身分註冊 — ${enrolling.row.display_name ?? enrolling.row.email}` : "身分註冊"}
        description="產生 24 小時有效的人臉 / Passkey 註冊連結，傳給對方完成綁定。"
      >
        {enrolling && (
          <EnrollResolver row={enrolling.row} tenantId={tenantParam} />
        )}
      </Drawer>

      <BindingCodeIssueModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onIssued={() => void loadPendingInvites()}
      />
    </>
  );
}

/** A MembershipRow has no officer_id field. Resolve via line_user_id
 *  first (canonical post-binding), display_name fallback (legacy /
 *  email-invited path). Shared by EnrollResolver / AssignSitesForm /
 *  UserDevicesTab. */
function findOfficerForMembership(row: MembershipRow, officers: readonly OfficerRow[]): OfficerRow | undefined {
  if (row.line_user_id) {
    const m = officers.find((o) => o.line_user_id === row.line_user_id);
    if (m) return m;
  }
  return officers.find((o) => o.display_name === (row.display_name ?? ""));
}

/** Looks up officer.id from MembershipRow then renders the
 *  OfficerEnrollDrawer body. */
function EnrollResolver({ row, tenantId }: { row: MembershipRow; tenantId: string | null }): React.ReactElement {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [officerId, setOfficerId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const officersRes = await listOfficers({ tenantId, status: "active" });
        if (cancelled) return;
        const matched = findOfficerForMembership(row, officersRes.items);
        if (!matched) {
          setErr("找不到此使用者的執勤者紀錄。請確認對方已完成 LINE 綁定；若已綁定仍出現此訊息，請聯絡 SENTRY 支援人員協助補建紀錄。");
          setPhase("error");
          return;
        }
        setOfficerId(matched.id);
        setPhase("ready");
      } catch (e) {
        setErr(friendlyUsersError(e));
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [row.line_user_id, row.display_name, tenantId]);

  if (phase === "loading") return <div className="text-xs text-text-muted">載入中…</div>;
  if (phase === "error" || !officerId) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
        {err ?? "載入失敗"}
      </div>
    );
  }
  return (
    <OfficerEnrollDrawer
      officerId={officerId}
      officerName={row.display_name ?? row.email}
      tenantId={tenantId}
    />
  );
}

function UserForm({ row, tenantId, onClose, onSaved }: {
  row: MembershipRow; tenantId: string | null; onClose: () => void; onSaved: () => void;
}): React.ReactElement {
  // 「裝置」 tab 取代原本獨立的 /dashboard/devices sidebar 條目，per-user
  // 視角的 Passkey 管理改在這裡；跨 user audit list 仍走 /dashboard/devices。
  // 「角色」 tab — Custom RBAC P6（spec §6.4）：把 user 跟自訂角色（或
  // 系統角色 + 可選 scope）的綁定關係搬進這個抽屜，取代「基本資料」 內
  // 單一 role 的 legacy 路徑（仍保留以便系統 sys_* role 可調整）。
  const [tab, setTab] = useState<"basic" | "roles" | "devices">("basic");

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-bg-base/40 p-1 inline-flex gap-1">
        <button
          type="button"
          onClick={() => setTab("basic")}
          className={[
            "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            tab === "basic" ? "bg-accent/15 text-accent" : "text-text-secondary hover:bg-bg-surface",
          ].join(" ")}
        >
          基本資料
        </button>
        <button
          type="button"
          onClick={() => setTab("roles")}
          className={[
            "px-3 py-1.5 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-1",
            tab === "roles" ? "bg-accent/15 text-accent" : "text-text-secondary hover:bg-bg-surface",
          ].join(" ")}
        >
          <ShieldCheck className="h-3 w-3" /> 角色
        </button>
        <button
          type="button"
          onClick={() => setTab("devices")}
          className={[
            "px-3 py-1.5 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-1",
            tab === "devices" ? "bg-accent/15 text-accent" : "text-text-secondary hover:bg-bg-surface",
          ].join(" ")}
        >
          <Smartphone className="h-3 w-3" /> 裝置
        </button>
      </div>
      {tab === "basic" && <UserFormBasic row={row} tenantId={tenantId} onClose={onClose} onSaved={onSaved} />}
      {tab === "roles" && <UserRolesTab userId={row.user_id} tenantId={tenantId} />}
      {tab === "devices" && <UserDevicesTab row={row} tenantId={tenantId} />}
    </div>
  );
}

function UserFormBasic({ row, tenantId, onClose, onSaved }: {
  row: MembershipRow; tenantId: string | null; onClose: () => void; onSaved: () => void;
}): React.ReactElement {
  const [role, setRole] = useState<UserRoleV2>((row.role as UserRoleV2) || "officer");
  const [status, setStatus] = useState<UserStatus>(row.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Synthetic id ("line:<...>") flags a LINE-only member backed by
  // line_integrations, not tenant_memberships. line_integrations only
  // has 'active' / 'disabled' status — there's no 'invited' equivalent.
  const isLineOnly = row.id.startsWith("line:");
  const subjectLabel = row.display_name || row.email || "此使用者";

  const save = async () => {
    setBusy(true); setErr(null);
    try { await patchMembership(row.id, { role, status }, tenantId); onSaved(); }
    catch (e) { setErr(friendlyUsersError(e)); } finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const revoke = async () => {
    if (!(await dlg.confirm(`確定要停用「${subjectLabel}」？此使用者將無法再登入此單位的後台。`, { destructive: true, okLabel: "停用" }))) return;
    setBusy(true); setErr(null);
    try { await revokeMembership(row.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyUsersError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="Email">
        <input
          className="input"
          disabled
          value={row.email || ""}
          placeholder={isLineOnly ? "（LINE 綁定 · 無 email）" : ""}
        />
      </Field>
      <Field label="角色">
        <select className="select" value={role} onChange={(e) => setRole(e.target.value as UserRoleV2)}>
          {(["tenant_admin", "manager", "site_supervisor", "officer", "incident_reporter", "auditor"] as UserRoleV2[]).map((r) => (
            <option key={r} value={r}>{ROLE_LABEL(r)}</option>
          ))}
        </select>
      </Field>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as UserStatus)}>
          <option value="active">啟用</option>
          {!isLineOnly && <option value="invited">已邀請（尚未登入）</option>}
          <option value="revoked">停用</option>
        </select>
      </Field>
      {row.super_admin && (
        <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs text-warning flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>此使用者具備 super_admin 權限。停用此租戶下的成員資格不會影響其平台管理員身分。</span>
        </div>
      )}
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <button className="btn-danger text-sm" onClick={() => void revoke()} disabled={busy}>
          <Trash2 className="h-4 w-4" /> 停用
        </button>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button onClick={() => void save()} className="btn-primary text-sm" disabled={busy}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function UserDevicesTab({ row, tenantId }: { row: MembershipRow; tenantId: string | null }): React.ReactElement {
  const [phase, setPhase] = useState<"loading" | "no_officer" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const dlg = useConfirmDialog();

  const load = useCallback(async () => {
    setPhase("loading"); setErr(null);
    try {
      const officersRes = await listOfficers({ tenantId, status: "active" });
      const matched = findOfficerForMembership(row, officersRes.items);
      if (!matched) { setPhase("no_officer"); return; }
      const dr = await listAdminDevices({ tenantId, officerId: matched.id, limit: 50 });
      setDevices(dr.items);
      setPhase("ready");
    } catch (e) {
      setErr(friendlyUsersError(e));
      setPhase("error");
    }
  }, [row, tenantId]);

  useEffect(() => { void load(); }, [load]);

  const toggle = async (d: DeviceRow) => {
    const target: DeviceStatus = d.status === "active" ? "revoked" : "active";
    const verb = target === "revoked" ? "停用" : "重新啟用";
    if (!(await dlg.confirm(`確定要${verb}「${d.device_model ?? "裝置"}」？`, { destructive: target === "revoked", okLabel: verb }))) return;
    setBusy(d.id);
    try { await patchDeviceStatus(d.id, target, tenantId); await load(); }
    catch (e) { await dlg.alert(friendlyUsersError(e), { title: "操作失敗" }); } finally { setBusy(null); }
  };

  if (phase === "loading") {
    return <div className="text-xs text-text-muted">載入中…</div>;
  }
  if (phase === "no_officer") {
    return (
      <div className="rounded border border-border bg-bg-base/40 p-4 text-xs text-text-secondary">
        此使用者沒有對應的執勤者紀錄，無 Passkey 裝置。請在「基本資料」 tab 將角色設為巡邏員或案場督導後，引導對方完成 LINE 綁定 + Passkey 註冊。
      </div>
    );
  }
  if (phase === "error") {
    return <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err ?? "載入失敗"}</div>;
  }
  if (devices.length === 0) {
    return (
      <div className="rounded border border-border bg-bg-base/40 p-4 text-xs text-text-secondary">
        此執勤者尚未綁定 Passkey 裝置。請至「基本資料」上方的「註冊」 入口產生 24 小時內可用的 enroll 連結。
        <div className="mt-2 text-2xs text-text-muted">
          想看跨使用者的裝置列表？開啟{" "}
          <Link to="/dashboard/devices" className="text-accent hover:text-accent-hover">/dashboard/devices</Link>。
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {devices.map((d) => (
        <div key={d.id} className="rounded border border-border bg-bg-base/40 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Smartphone className="h-4 w-4 text-text-muted flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-text-primary truncate">{d.device_model ?? "未命名裝置"}</div>
                <div className="text-2xs text-text-muted truncate">{d.os ?? d.user_agent?.slice(0, 40) ?? "—"}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {d.webauthn_bound && (
                <span className="badge-success inline-flex items-center gap-1 text-2xs"><KeyRound className="h-3 w-3" />Passkey</span>
              )}
              <span className={d.status === "active" ? "badge-success" : "badge-default"}>
                {d.status === "active" ? "啟用" : "已停用"}
              </span>
              <button
                className="btn-ghost text-xs h-7"
                onClick={() => void toggle(d)}
                disabled={busy === d.id}
              >
                {d.status === "active"
                  ? <><Ban className="h-3.5 w-3.5" /> 停用</>
                  : <><CheckCircle2 className="h-3.5 w-3.5" /> 啟用</>}
              </button>
            </div>
          </div>
          <div className="mt-2 text-2xs text-text-muted">
            最後上線 {formatTaipeiAgo(d.last_seen_at)} · 註冊於 {d.enrolled_at.slice(0, 10)}
          </div>
        </div>
      ))}
      <div className="text-2xs text-text-muted pt-2">
        想看跨使用者的裝置列表？開啟{" "}
        <Link to="/dashboard/devices" className="inline-flex items-center gap-1 text-accent hover:text-accent-hover">
          /dashboard/devices <ExternalLink className="h-3 w-3" />
        </Link>。
      </div>
    </div>
  );
}

function AssignSitesForm({ row, tenantId, onClose, onSaved }: {
  row: MembershipRow; tenantId: string | null; onClose: () => void; onSaved: () => void;
}): React.ReactElement {
  const [phase, setPhase] = useState<"loading" | "ready" | "saving" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [officer, setOfficer] = useState<OfficerRow | null>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // 1. Resolve officer.id by display_name + line_user_id match.
  // 2. Fetch all tenant sites + officer's current assignments in parallel.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [officersRes, sitesRes] = await Promise.all([
          listOfficers({ tenantId, status: "active" }),
          listSites({ tenantId, status: "active", limit: 500 }),
        ]);
        if (cancelled) return;
        const matched = findOfficerForMembership(row, officersRes.items);
        if (!matched) {
          setErr("找不到此使用者的執勤者紀錄。請確認對方已完成 LINE 綁定；若已綁定仍出現此訊息，請聯絡 SENTRY 支援人員協助補建紀錄。");
          setPhase("error");
          return;
        }
        setOfficer(matched);
        setSites(sitesRes.items);
        const cur = await getOfficerSites(matched.id, tenantId);
        if (cancelled) return;
        setSelected(new Set(cur.items.map((a) => a.site_id)));
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setErr(friendlyUsersError(e));
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [row.line_user_id, row.display_name, tenantId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!officer) return;
    setPhase("saving"); setErr(null);
    try {
      await putOfficerSites(officer.id, [...selected], tenantId);
      onSaved();
    } catch (e) {
      setErr(friendlyUsersError(e));
      setPhase("ready");
    }
  };

  if (phase === "loading") {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="space-y-3">
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err ?? "載入失敗"}</div>
        <button onClick={onClose} className="btn-secondary text-sm">關閉</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-2xs text-text-muted">
        共 {sites.length} 個案場，已選 {selected.size} 個。
      </div>
      <div className="rounded border border-border bg-surface-0 max-h-96 overflow-y-auto divide-y divide-border">
        {sites.length === 0 && (
          <div className="p-4 text-sm text-text-muted">此單位尚未建立任何案場。請先到「案場」頁建立案場。</div>
        )}
        {sites.map((s) => (
          <label key={s.id} className="flex items-center gap-3 p-3 hover:bg-surface-1 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.has(s.id)}
              onChange={() => toggle(s.id)}
              className="h-4 w-4"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-text-primary truncate">{s.name}</div>
              <div className="text-2xs text-text-muted truncate">
                {s.client_name ?? "—"}{s.address ? ` · ${s.address}` : ""}
              </div>
            </div>
            <span className={`badge-default text-2xs ${s.risk_level === "high" ? "badge-danger" : s.risk_level === "medium" ? "badge-warning" : ""}`}>
              {s.risk_level === "high" ? "高" : s.risk_level === "medium" ? "中" : "低"}
            </span>
          </label>
        ))}
      </div>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="btn-secondary text-sm" disabled={phase === "saving"}>取消</button>
        <button onClick={() => void save()} className="btn-primary text-sm" disabled={phase === "saving" || sites.length === 0}>
          {phase === "saving" ? "儲存中…" : "儲存指派"}
        </button>
      </div>
    </div>
  );
}

function UsersSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-7 w-7 rounded-full" />
            <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-12 hidden sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
